import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import DrawerWrapper, { DrawerFooter } from '../IAP/DrawerWrapper';
import { StylishNewButton } from '../DesignSystems/New/StylishNewButton';
import { DGPTAlertCategory } from '../../types/dgptAlerts';
import { useUpdateAlertCategory } from './hooks/useAlertCategories';
import { useDistributionLists } from '../../hooks/useDistributionLists';
import { useSelector } from 'react-redux';
import { selectGroupGuid } from '../../slices/commonSelectors';
import { Row } from 'react-bootstrap';
import FormCheckboxField from '../IAP/formFields/FormCheckboxField';
import { LockedUpgradeOption } from 'components/Notifications/LockedUpgradeOption';

const LOCKED_CONTACT_METHOD = 'Text-To-Speech (cell)';

type AlertCategoryEditDrawerProps = {
  onClose: () => void;
  category: DGPTAlertCategory | null;
};

type FormFields = {
  contactMethods: string[];
  teams: string[];
  ack: boolean;
};

const CONTACT_METHODS = [
  'Email',
  'SMS',
  'Text-To-Speech (cell)',
  'Text-To-Speech (home)',
];

const AlertCategoryEditDrawer: React.FC<AlertCategoryEditDrawerProps> = ({
  onClose,
  category,
}) => {
  const groupGuid = useSelector(selectGroupGuid);
  const { data: teams = [] } = useDistributionLists(groupGuid);
  const updateCategory = useUpdateAlertCategory();

  const methods = useForm<FormFields>({
    defaultValues: {
      contactMethods: [],
      teams: [],
      ack: false,
    },
  });

  const { handleSubmit, reset, setValue, watch } = methods;
  const watchedContactMethods = watch('contactMethods');
  const watchedTeams = watch('teams');

  useEffect(() => {
    if (category) {
      reset({
        contactMethods: category.contactMethods || [],
        teams: category.teams || [],
        ack: category.ack || false,
      });
    }
  }, [category, reset]);

  const onSubmit = (data: FormFields) => {
    if (!category) return;

    updateCategory.mutate(
      {
        id: category.id,
        contactMethods: data.contactMethods.filter(
          (method) => method !== LOCKED_CONTACT_METHOD
        ),
        teams: data.teams,
        ack: data.ack,
        severity: category.severity,
      },
      {
        onSuccess: () => {
          onClose();
        },
      }
    );
  };

  const handleContactMethodToggle = (method: string) => {
    if (method === LOCKED_CONTACT_METHOD) return;
    const currentMethods = watchedContactMethods;
    const newMethods = currentMethods.includes(method)
      ? currentMethods.filter((m) => m !== method)
      : [...currentMethods, method];
    setValue('contactMethods', newMethods);
  };

  const handleTeamToggle = (teamId: string) => {
    const currentTeams = watchedTeams;
    const newTeams = currentTeams.includes(teamId)
      ? currentTeams.filter((t) => t !== teamId)
      : [...currentTeams, teamId];
    setValue('teams', newTeams);
  };

  if (!category) return null;

  return (
    <DrawerWrapper
      toggle={onClose}
      title={`Edit Severity ${category.severity} Category`}
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="p-4">
            <Row>
              <div className="col-12">
                <h6 className="mb-3">Contact Methods</h6>
                <div className="d-flex mb-4 flex-wrap gap-2">
                  {CONTACT_METHODS.map((method) =>
                    method === LOCKED_CONTACT_METHOD ? (
                      <LockedUpgradeOption
                        key={method}
                        label="Voice (TTS cell)"
                      />
                    ) : (
                      <div key={method} className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id={`contact-${method}`}
                          checked={watchedContactMethods.includes(method)}
                          onChange={() => handleContactMethodToggle(method)}
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`contact-${method}`}
                        >
                          {method}
                        </label>
                      </div>
                    )
                  )}
                </div>
              </div>
            </Row>

            <Row>
              <div className="col-12">
                <h6 className="mb-3">Teams</h6>
                <div className="d-flex mb-4 flex-wrap gap-2">
                  {teams.map((team) => (
                    <div key={team.id} className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={`team-${team.id}`}
                        checked={watchedTeams.includes(team.id)}
                        onChange={() => handleTeamToggle(team.id)}
                      />
                      <label
                        className="form-check-label"
                        htmlFor={`team-${team.id}`}
                      >
                        {team.name}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </Row>

            <Row>
              <div className="col-12">
                <FormCheckboxField
                  title="Acknowledgment Required"
                  fieldName="ack"
                />
              </div>
            </Row>
          </div>

          <DrawerFooter>
            <div className="button-group">
              <StylishNewButton
                className="button--secondary button--reverse"
                type="button"
                onClick={onClose}
              >
                Cancel
              </StylishNewButton>
              <StylishNewButton
                className="button--primary"
                type="submit"
                disabled={updateCategory.isPending}
              >
                Save Changes
              </StylishNewButton>
            </div>
          </DrawerFooter>
        </form>
      </FormProvider>
    </DrawerWrapper>
  );
};

export default AlertCategoryEditDrawer;
